import { useState, useEffect } from 'react';
import { Coins, Check, Sparkles, X, Loader2, Globe, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { creditsAPI } from '../lib/api';
import { toast } from 'sonner';

export default function BuyCreditsModal({ isOpen, onClose, onSuccess }) {
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [countryCode, setCountryCode] = useState(null);
  const [pppDiscount, setPppDiscount] = useState(0);
  const [creditCosts, setCreditCosts] = useState({});

  useEffect(() => {
    if (isOpen) {
      fetchBundles();
    }
  }, [isOpen]);

  const fetchBundles = async () => {
    try {
      setLoading(true);
      const response = await creditsAPI.getBalance();
      const data = response.data || response;  // Handle both raw data and axios response
      setBundles(data.bundles || []);
      setCountryCode(data.country_code);
      setCreditCosts(data.credit_costs || {});
      
      // Calculate PPP discount percentage
      const ppp = data.ppp_multiplier || 1.0;
      if (ppp < 1.0) {
        setPppDiscount(Math.round((1 - ppp) * 100));
      }
    } catch (err) {
      console.error('Failed to fetch bundles:', err);
      toast.error('Failed to load credit bundles');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (bundleId) => {
    try {
      setPurchasing(bundleId);
      const response = await creditsAPI.purchase(bundleId);
      const result = response.data || response;
      
      if (result.checkout_url) {
        // Redirect to Mollie checkout
        window.location.href = result.checkout_url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (err) {
      console.error('Purchase error:', err);
      toast.error(err.response?.data?.detail || 'Purchase failed');
    } finally {
      setPurchasing(null);
    }
  };

  const getBundleIcon = (bundleId) => {
    switch (bundleId) {
      case 'starter_10': return '🌱';
      case 'creator_50': return '🎨';
      case 'pro_150': return '🚀';
      case 'studio_500': return '🎬';
      default: return '📦';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-gray-900 border-gray-700 text-white" data-testid="buy-credits-modal">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-500/20 rounded-lg">
                <Coins className="w-6 h-6 text-yellow-500" />
              </div>
              <div>
                <DialogTitle className="text-xl">Buy Credits</DialogTitle>
                <DialogDescription className="text-gray-400">
                  Pay as you go - no subscription required
                </DialogDescription>
              </div>
            </div>
          </div>
        </DialogHeader>

        {/* PPP Discount Banner */}
        {pppDiscount > 0 && (
          <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/30 rounded-lg" data-testid="ppp-discount-banner">
            <Globe className="w-5 h-5 text-green-400" />
            <div className="text-sm">
              <span className="text-green-400 font-medium">{pppDiscount}% regional discount</span>
              <span className="text-gray-400"> applied for your location</span>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : (
          <>
            {/* Credit Bundles Grid */}
            <div className="grid grid-cols-2 gap-4 mt-4" data-testid="credit-bundles-grid">
              {bundles.map((bundle) => (
                <div
                  key={bundle.id}
                  className={`relative p-4 rounded-xl border transition-all ${
                    bundle.popular
                      ? 'border-yellow-500/50 bg-yellow-500/5'
                      : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
                  }`}
                  data-testid={`credit-bundle-${bundle.id}`}
                >
                  {bundle.popular && (
                    <Badge className="absolute -top-2 left-4 bg-yellow-500 text-black text-xs" data-testid="popular-badge">
                      <Sparkles className="w-3 h-3 mr-1" />
                      Most Popular
                    </Badge>
                  )}

                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <span className="text-2xl">{getBundleIcon(bundle.id)}</span>
                      <h3 className="text-lg font-semibold mt-1" data-testid={`bundle-name-${bundle.id}`}>{bundle.name}</h3>
                    </div>
                    {bundle.savings_percent > 0 && (
                      <Badge variant="outline" className="text-green-400 border-green-400/30" data-testid={`bundle-savings-${bundle.id}`}>
                        Save {bundle.savings_percent}%
                      </Badge>
                    )}
                  </div>

                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-3xl font-bold" data-testid={`bundle-price-${bundle.id}`}>${bundle.price.toFixed(2)}</span>
                    {bundle.original_price && bundle.original_price > bundle.price && (
                      <span className="text-sm text-gray-500 line-through" data-testid={`bundle-original-price-${bundle.id}`}>
                        ${bundle.original_price.toFixed(2)}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2 text-sm text-gray-400 mb-4">
                    <Coins className="w-4 h-4 text-yellow-500" />
                    <span data-testid={`bundle-credits-${bundle.id}`}>{bundle.credits} credits</span>
                    <span className="text-gray-600">•</span>
                    <span data-testid={`bundle-price-per-credit-${bundle.id}`}>${bundle.price_per_credit}/credit</span>
                  </div>

                  <Button
                    className={`w-full ${
                      bundle.popular
                        ? 'bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-black'
                        : 'bg-gray-700 hover:bg-gray-600'
                    }`}
                    onClick={() => handlePurchase(bundle.id)}
                    disabled={purchasing === bundle.id}
                    data-testid={`buy-bundle-btn-${bundle.id}`}
                  >
                    {purchasing === bundle.id ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>Buy {bundle.credits} Credits</>
                    )}
                  </Button>
                </div>
              ))}
            </div>

            {/* Credit Usage Info */}
            <div className="mt-6 p-4 bg-gray-800/50 rounded-lg">
              <h4 className="text-sm font-medium text-gray-300 mb-3 flex items-center gap-2">
                <Zap className="w-4 h-4 text-yellow-500" />
                What can you create with credits?
              </h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex justify-between text-gray-400">
                  <span>Fast Video</span>
                  <span className="text-white">{creditCosts.fast_video || 1} credit</span>
                </div>
                <div className="flex justify-between text-gray-400">
                  <span>Premium Video</span>
                  <span className="text-white">{creditCosts.premium_video || 3} credits</span>
                </div>
                <div className="flex justify-between text-gray-400">
                  <span>UGC Ad (Standard)</span>
                  <span className="text-white">{creditCosts.ugc_standard || 2} credits</span>
                </div>
                <div className="flex justify-between text-gray-400">
                  <span>UGC Ad (Pro)</span>
                  <span className="text-white">{creditCosts.ugc_pro || 5} credits</span>
                </div>
                <div className="flex justify-between text-gray-400">
                  <span>Viral Maker</span>
                  <span className="text-white">{creditCosts.viral_maker || 2} credits</span>
                </div>
                <div className="flex justify-between text-gray-400">
                  <span>Voice Clone</span>
                  <span className="text-white">{creditCosts.voice_clone || 1} credit</span>
                </div>
              </div>
            </div>

            {/* Daily Free Credit Info */}
            <div className="text-center text-sm text-gray-500 mt-4">
              <Sparkles className="w-4 h-4 inline mr-1 text-yellow-500" />
              Everyone gets 1 free credit daily • Credits never expire
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
